import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { 
  Loader2, ArrowLeft, RefreshCw, Trash2, UserX, Shield, Users
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface AdminUser {
  id: string;
  user_id: string;
  role: 'admin' | 'user';
  email?: string;
}

const AdminUsers = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetchAdmins = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('id, user_id, role')
        .eq('role', 'admin');

      if (error) throw error;
      
      // Map the data to AdminUser format
      const adminUsers: AdminUser[] = (data || []).map(role => ({
        id: role.id,
        user_id: role.user_id,
        role: role.role as 'admin' | 'user',
      }));
      
      setAdmins(adminUsers);
    } catch (err: any) {
      console.error('Error fetching admins:', err);
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/admin/login');
        return;
      }

      setCurrentUserId(session.user.id);

      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'admin')
        .single();

      if (!roles) {
        toast.error('Accesso non autorizzato');
        await supabase.auth.signOut();
        navigate('/admin/login');
        return;
      }

      fetchAdmins();
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        navigate('/admin/login');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, fetchAdmins]);

  const removeAdminRole = async (roleId: string, userId: string) => {
    if (userId === currentUserId) {
      toast.error('Non puoi rimuovere te stesso!');
      return;
    }

    setRemovingId(roleId);
    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', roleId);

      if (error) throw error;
      
      toast.success('Ruolo admin rimosso');
      fetchAdmins();
    } catch (err: any) {
      console.error('Error removing admin:', err);
      toast.error('Errore nella rimozione');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Users className="h-5 w-5" />
                Gestione Admin
              </h1>
              <p className="text-sm text-muted-foreground">{admins.length} amministratori</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAdmins}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Info Box */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
          <p className="text-sm text-muted-foreground">
            <Shield className="h-4 w-4 inline mr-2 text-primary" />
            Gli amministratori possono gestire ordini, prodotti e altri admin.
            Per aggiungere nuovi admin, usa il modulo di registrazione con il codice segreto.
          </p>
        </div>

        {/* Admin List */}
        {admins.length === 0 ? (
          <div className="text-center py-12">
            <UserX className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nessun amministratore trovato</p>
          </div>
        ) : (
          <div className="space-y-3">
            {admins.map((admin) => (
              <div 
                key={admin.id} 
                className="card-elevated p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">
                      {admin.user_id === currentUserId ? (
                        <span className="flex items-center gap-2">
                          {admin.user_id.slice(0, 8)}...
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Tu</span>
                        </span>
                      ) : (
                        `${admin.user_id.slice(0, 8)}...`
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ID: {admin.id.slice(0, 8)}...
                    </p>
                  </div>
                </div>

                {admin.user_id !== currentUserId && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-destructive border-destructive/30 hover:bg-destructive/10"
                        disabled={removingId === admin.id}
                      >
                        {removingId === admin.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Rimuovere questo admin?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Questo utente perderà l'accesso alla dashboard admin. 
                          Potrà registrarsi nuovamente se conosce il codice di registrazione.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => removeAdminRole(admin.id, admin.user_id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Rimuovi Admin
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add New Admin Link */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground mb-2">
            Vuoi aggiungere un nuovo amministratore?
          </p>
          <Button variant="outline" onClick={() => navigate('/admin/login')}>
            Vai alla pagina di registrazione
          </Button>
        </div>
      </main>
    </div>
  );
};

export default AdminUsers;
